import React, {
    FC, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState,
} from 'react';

import classNames from 'classnames';

import { PdfViewerClient } from '../client/pdfViewerClient';
import { ZoomController, findPageAt } from '../client/zoomController';
import {
    NARROW_QUERY,
    OVERVIEW_MARGIN,
    PAGE_PADDING,
    SCROLL_ANCHOR,
    SEARCH_SCROLL_OFFSET,
    TOUCH_QUERY,
    WHEEL_ZOOM_SPEED,
} from '../constants';
import { initialFitMode } from '../helpers/gestures';
import useMediaQuery, { matchesQuery } from '../hooks/useMediaQuery';
import usePanScroll from '../hooks/usePanScroll';
import usePdfSearch from '../hooks/usePdfSearch';
import useSharedObserver from '../hooks/useSharedObserver';
import useTouchZoom from '../hooks/useTouchZoom';
import { Watermarks } from '../hooks/useWatermarks';
import { FitMode, PageInfo } from '../types';
import PageView, { Highlight } from './PageView';
import SearchBar from './SearchBar';
import Sidebar from './Sidebar';
import Toolbar, { SidebarView } from './Toolbar';
import styles from '../PdfTileViewer.module.scss';

interface ViewerBodyProps {
    client: PdfViewerClient;
    pages: PageInfo[];
    watermarks: Watermarks;
    carouselMode: boolean;
}

const NO_HIGHLIGHTS: Highlight[] = [];
const VISIBLE_MARGIN = '0px';

const ViewerBody: FC<ViewerBodyProps> = ({
    client, pages, watermarks, carouselMode,
}) => {
    const scrollerRef = useRef<HTMLDivElement>(null);
    const pagesRef = useRef<HTMLDivElement>(null);
    const [controller, setController] = useState<ZoomController | null>(null);
    const [startFitMode] = useState<FitMode>(() => initialFitMode(matchesQuery(NARROW_QUERY)));
    const [fitMode, setFitMode] = useState<FitMode | null>(startFitMode);
    const [current, setCurrentState] = useState<number>(0);
    const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(false);
    const [sidebarView, setSidebarView] = useState<SidebarView>('thumbnails');
    const [isPanActive, setIsPanActive] = useState<boolean>(false);
    const [isSearchOpen, setIsSearchOpen] = useState<boolean>(false);
    const [searchFocus, setSearchFocus] = useState<number>(0);

    const currentRef = useRef<number>(0);
    const controllerRef = useRef<ZoomController | null>(null);

    const observeNear = useSharedObserver(scrollerRef, OVERVIEW_MARGIN);
    const observeVisible = useSharedObserver(scrollerRef, VISIBLE_MARGIN);
    const isDragging = usePanScroll(scrollerRef, isPanActive);
    const search = usePdfSearch(client);
    const isNarrow = useMediaQuery(NARROW_QUERY);
    const isTouch = useMediaQuery(TOUCH_QUERY);

    const setCurrent = useCallback((index: number) => {
        if (index === currentRef.current) {
            return;
        }
        currentRef.current = index;
        setCurrentState(index);
        if (controllerRef.current) {
            client.prefetchAround(index, pages, controllerRef.current.zoom);
        }
    }, [client, pages]);

    useLayoutEffect(() => {
        const zoomController = new ZoomController({
            scroller: scrollerRef.current,
            pagesElement: pagesRef.current,
            pages,
            dpr: client.dpr,
            getCurrent: () => currentRef.current,
            onFitModeChange: setFitMode,
            initialFitMode: startFitMode,
            ignoreHeightChanges: matchesQuery(TOUCH_QUERY),
        });
        controllerRef.current = zoomController;
        const unsubscribe = zoomController.onSettle(() => {
            client.prefetchAround(currentRef.current, pages, zoomController.zoom);
        });
        client.prefetchAround(0, pages, zoomController.zoom);
        setController(zoomController);
        return () => {
            unsubscribe();
            zoomController.dispose();
            controllerRef.current = null;
        };
    }, [client, pages]);

    useEffect(() => {
        const scroller = scrollerRef.current;
        if (!controller) {
            return undefined;
        }
        const onWheel = (event: WheelEvent): void => {
            if (!event.ctrlKey && !event.metaKey) {
                return;
            }
            event.preventDefault();
            controller.wheelZoom(
                Math.exp(-event.deltaY * WHEEL_ZOOM_SPEED),
                event.clientY - scroller.getBoundingClientRect().top,
            );
        };
        scroller.addEventListener('wheel', onWheel, { passive: false });
        return () => scroller.removeEventListener('wheel', onWheel);
    }, [controller]);

    useTouchZoom(scrollerRef, controller, isTouch);

    const goTo = useCallback((requested: number) => {
        const zoomController = controllerRef.current;
        if (!zoomController) {
            return;
        }
        const index = Math.min(Math.max(requested, 0), pages.length - 1);
        scrollerRef.current.scrollTop = zoomController.tops[index] - PAGE_PADDING;
        setCurrent(index);
    }, [pages, setCurrent]);

    const onScroll = (): void => {
        const scroller = scrollerRef.current;
        if (controller?.tops.length) {
            setCurrent(findPageAt(controller.tops, scroller.scrollTop + scroller.clientHeight * SCROLL_ANCHOR));
        }
    };

    const selectFromSidebar = (index: number): void => {
        goTo(index);
        if (isNarrow) {
            setIsSidebarOpen(false);
        }
    };

    const openSidebarView = (view: SidebarView): void => {
        setSidebarView(view);
        setIsSidebarOpen(true);
    };

    const toggleSearch = (force?: boolean): void => {
        const open = force === undefined ? !isSearchOpen : force;
        setIsSearchOpen(open);
        if (open) {
            setSearchFocus((value) => value + 1);
        } else {
            search.reset();
        }
    };

    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent): void => {
            if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'f') {
                event.preventDefault();
                setIsSearchOpen(true);
                setSearchFocus((value) => value + 1);
            }
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, []);

    const onScrollerKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
        if ((event.target as HTMLElement).tagName === 'INPUT') {
            return;
        }
        if (event.key === 'ArrowRight' || event.key === 'PageDown') {
            goTo(currentRef.current + 1);
        }
        if (event.key === 'ArrowLeft' || event.key === 'PageUp') {
            goTo(currentRef.current - 1);
        }
    };

    useEffect(() => {
        const result = search.state.results[search.state.active];
        const zoomController = controllerRef.current;
        if (!search.jump || !result || !zoomController) {
            return;
        }
        const rect = result.rectsOnPage[0];
        const scroller = scrollerRef.current;
        const offset = rect ? rect[0] * zoomController.zoom : 0;
        scroller.scrollTop = Math.max(
            0,
            zoomController.tops[result.pageIndex] + offset - scroller.clientHeight / SEARCH_SCROLL_OFFSET,
        );
        setCurrent(result.pageIndex);
    }, [search.jump]);

    const highlightsByPage = useMemo(() => {
        const map = new Map<number, Highlight[]>();
        search.state.results.forEach((result, resultIndex) => {
            const list = map.get(result.pageIndex) ?? [];
            result.rectsOnPage.forEach((rect) => list.push({
                rect, isActive: resultIndex === search.state.active,
            }));
            map.set(result.pageIndex, list);
        });
        return map;
    }, [search.state.results, search.state.active]);

    return (
        <>
            <Toolbar
                current={current}
                total={pages.length}
                fitMode={fitMode}
                isPanActive={isPanActive}
                hasPan={!isTouch}
                isSearchOpen={isSearchOpen}
                carouselMode={carouselMode}
                onToggleSidebar={() => setIsSidebarOpen((value) => !value)}
                onSidebarView={openSidebarView}
                onGoTo={goTo}
                onTogglePan={() => setIsPanActive((value) => !value)}
                onZoomIn={() => controller?.stepZoom(1)}
                onZoomOut={() => controller?.stepZoom(-1)}
                onToggleFit={() => controller?.toggleFit()}
                onToggleSearch={() => toggleSearch()}
            />
            <div className={styles.body}>
                <Sidebar
                    client={client}
                    pages={pages}
                    current={current}
                    isOpen={isSidebarOpen}
                    view={sidebarView}
                    onSelect={selectFromSidebar}
                    onClose={() => setIsSidebarOpen(false)}
                />
                <div
                    ref={scrollerRef}
                    className={classNames(styles.scroller, {
                        [styles.pan]: isPanActive,
                        [styles.dragging]: isDragging,
                    })}
                    role="presentation"
                    tabIndex={-1}
                    onKeyDown={onScrollerKeyDown}
                    onScroll={onScroll}
                >
                    <div ref={pagesRef} className={styles.pages}>
                        {controller && pages.map((page, index) => (
                            <PageView
                                key={page.page}
                                index={index}
                                page={page}
                                controller={controller}
                                client={client}
                                watermarks={watermarks}
                                highlights={highlightsByPage.get(index) ?? NO_HIGHLIGHTS}
                                observeNear={observeNear}
                                observeVisible={observeVisible}
                            />
                        ))}
                    </div>
                </div>
                {isSearchOpen && (
                    <SearchBar
                        focusSignal={searchFocus}
                        resultCount={search.state.results.length}
                        active={search.state.active}
                        isRunning={search.state.isRunning}
                        hasFailed={search.state.hasFailed}
                        onSearch={search.run}
                        onStep={search.step}
                        onClose={() => toggleSearch(false)}
                    />
                )}
            </div>
        </>
    );
};

export default ViewerBody;
